import React from 'react';
import './ServicesPage.css';
import '../scrollReveal.css';
import useScrollReveal from '../hooks/useScrollReveal';
import {
  FaHandshake, FaShieldAlt, FaClipboardList, FaFileSignature,
  FaTruck, FaCalendarCheck, FaCube, FaWrench, FaBolt,
  FaDraftingCompass, FaCogs, FaShapes,
} from 'react-icons/fa';

const services = [
  { title: 'Carbon Fiber Aero Components', desc: 'Front splitters, rear diffusers, side skirts, canards, and full aero kits engineered to your car\'s exact geometry for perfect fitment and real aerodynamic performance.', icon: <FaCube /> },
  { title: 'Custom Body Kits & Wide-Bodies', desc: 'One-off widebody conversions, custom fender flares, and full restyling packages designed from scratch or based on your vision. Built to OEM-quality standards.', icon: <FaShapes /> },
  { title: 'Carbon Fiber Interior Trim', desc: 'Bespoke carbon fiber interior panels, dashboard trim, center consoles, door cards, and gauge clusters — the finishing touch every exotic deserves.', icon: <FaDraftingCompass /> },
  { title: 'Intake & Cooling Systems', desc: 'Custom intake plenums, air boxes, heat shields, and cooling ducts 3D printed in high-temp materials. Engineered for airflow, not just aesthetics.', icon: <FaBolt /> },
  { title: '3D Scanning & Part Replication', desc: 'Laser-scan your OEM or aftermarket parts to create exact digital replicas. Ideal for discontinued components, custom one-offs, and parts that must fit within factory tolerances.', icon: <FaCogs /> },
  { title: 'Rapid Prototype & R&D Support', desc: 'Concept to prototype in days. Whether you\'re an aftermarket brand developing a new product line or building a show car, we move fast without cutting corners.', icon: <FaWrench /> },
];

const steps = [
  { icon: <FaHandshake />, label: 'Step 01', title: 'Send Your Vision', desc: 'Share reference photos, sketches, or just a description of what you want. We work with everything from a napkin sketch to a detailed brief.' },
  { icon: <FaShieldAlt />, label: 'Step 02', title: 'Custom Quote', desc: 'We review your project and send a detailed quote within 24 hours — materials, timeline, and pricing, all in plain language.' },
  { icon: <FaClipboardList />, label: 'Step 03', title: 'Design & Engineering', desc: 'Our team models your parts in Fusion 360, sharing 3D previews for your approval before anything gets made.' },
  { icon: <FaFileSignature />, label: 'Step 04', title: 'Fabrication', desc: 'We print, finish, and quality-check every piece to precision tolerances. No shortcuts, no off-spec parts.' },
  { icon: <FaCalendarCheck />, label: 'Step 05', title: 'Fitment Review', desc: 'Before shipping, we verify fitment against your car\'s exact specifications and make any final adjustments.' },
  { icon: <FaTruck />, label: 'Step 06', title: 'Delivery', desc: 'Parts are carefully packaged and shipped. Most builds ship within 5–10 business days of approval.' },
];

const ServicesPage = () => {
  const bannerRef = useScrollReveal();
  const heroTextRef = useScrollReveal();
  const servicesRef = useScrollReveal();
  const stepsRef = useScrollReveal();

  return (
    <div className="services-page">
      {/* Intro Banner */}
      <section className="intro-banner" ref={bannerRef}>
        <div className="intro-content">
          <div className="intro-eyebrow" data-reveal="fade-down">KING3DMFG</div>
          <h1 data-reveal="fade-up" data-delay="150">
            Built for<br />
            <span className="highlight">Exotic Cars</span>
          </h1>
          <p data-reveal="fade-up" data-delay="250">
            Every service we offer is purpose-built for high-performance and exotic cars. We don't do generic — we build exactly what your car needs.
          </p>
          <div className="badges" data-reveal="fade-up" data-delay="350">
            <span className="badge">Lamborghini</span>
            <span className="badge">Ferrari</span>
            <span className="badge">McLaren</span>
            <span className="badge">Porsche</span>
          </div>
        </div>
      </section>

      {/* Section Header */}
      <div className="services-hero" ref={heroTextRef}>
        <div className="section-eyebrow" data-reveal="fade-down">What We Build</div>
        <h1 data-reveal="fade-up" data-delay="100">Our Services</h1>
        <p data-reveal="fade-up" data-delay="200">
          From carbon fiber aero kits to one-off custom body work — precision fabrication for the world's most exclusive vehicles.
        </p>
      </div>

      {/* Services Grid */}
      <section className="services-section" ref={servicesRef}>
        <div className="services-grid">
          {services.map((svc, idx) => (
            <div key={idx} className="service-card" data-reveal="fade-up" data-delay={String(100 + (idx % 3) * 100)}>
              <span className="service-icon">{svc.icon}</span>
              <h2>{svc.title}</h2>
              <p>{svc.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Process Steps */}
      <section className="getting-started" ref={stepsRef}>
        <div className="getting-started-inner">
          <div className="section-eyebrow" data-reveal="fade-down">Process</div>
          <h2 data-reveal="fade-up" data-delay="100">From Idea to Build</h2>
          <p data-reveal="fade-up" data-delay="200">
            A straightforward process designed around your timeline and vision — no runaround, no guesswork.
          </p>
          <div className="steps-grid">
            {steps.map((step, idx) => (
              <div key={idx} className="step-card" data-reveal="zoom-left" data-delay={String(100 + idx * 100)}>
                <span className="step-number">{step.label}</span>
                <span className="step-icon">{step.icon}</span>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="quote-footer">
        &copy; {new Date().getFullYear()} KING3DMFG — All Rights Reserved
      </footer>
    </div>
  );
};

export default ServicesPage;
